import React, { useCallback, useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CustomDataTable from "../../../components/CustomDataTable";
import CustomDropdown from "../../../components/CustomDropdown";
import Hoc from "../../../components/HOC";
import Loader from "../../../components/Loader";
import CustomToolTip from "../../../components/ToolTip";
import moment from "moment";
import {
  MdDownload,
  MdInfo,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdVisibility,
  MdAttachFile,
  MdKeyboardArrowUp,
  MdKeyboardArrowDown,
  MdCancel
} from "react-icons/md";
import { CalendarToday } from "@mui/icons-material";
import {
  requestGenerateInvoice,
  setResetGenerateInvoice,
  setResetStateToBeInvoicedTimesheetList,
  setResetStateToBeInvoicedTimesheetListByEmployeeAndProject,
  requestToBeInvoicedTimesheetListAction,
  requestToBeInvoicedTimesheetListByEmployeeAndProject,
  setResetDownloadFileById,
  requestDownloadFileById,
  requestTimesheetsAction,
  requestTimesheetListAction,
  setResetTimesheetsAction,
} from "../../../Redux/TimeSheetState/TimeSheetActionCreator";
import { requestUsersListActionWithOutPagination } from "../../../Redux/UserState/UserActionCreator";
import { requestProjectsById, setResetStateProjectsById } from "../../../Redux/ProjectState/ProjectActionCreator";
import TextInput from "../../../components/TextInput";
import ModalComponent from "../../../components/Modal";

const ToBeInvoiced = () => {
  const dispatch = useDispatch();
  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [isModalOpenView, setIsModalOpenView] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isMultipleMode, setIsMultipleMode] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [isFetchingProjects, setIsFetchingProjects] = useState(false);
  const [modalOpen, setModalOpen] = useState({ open: false, id: null });
  const [reason, setReason] = useState("");
  const formikRef = useRef(null);

  // Redux selectors
  const toBeInvoicedList = useSelector((state) => state.ToBeInvoicedListReducer);
  const toBeInvoicedByEmployeeAndProject = useSelector((state) => state.ToBeInvoicedTimesheetListByEmployeeAndProjectReducer);
 
  const TimesheetAction = useSelector((state) => state.TimesheetsActionReducer);
  const usersList = useSelector((state) => state.UsersListWithOutPagination);
  const projectsList = useSelector((state) => state.ProjectsByIdReducer);
  const generateInvoice = useSelector((state) => state.GenerateInvoiceReducer);
  const downloadFileById = useSelector((state) => state.DownloadFileByIdReducer);
  const [sortBy, setSortBy] = useState(null); 

  

  const handleEndDateSort = () => {
    const newSortBy = sortBy === 'desc' ? null : 'desc';
    setSortBy(newSortBy);
   
  };

  const handleCheckboxChange = useCallback((state) => {
    const newSelectedRows = state.selectedRows;

    if (newSelectedRows.length === 0) {
      setSelectedProject(null);
      setSelectedUser(null);
      setSelectedRows([]);
      return;
    }

    if (isMultipleMode) {
      // Multiple mode: allow multiple selections
      setSelectedRows(newSelectedRows);
      // Set project and user based on the first selected row for display purposes
      if (newSelectedRows.length > 0) {
        setSelectedProject(newSelectedRows[0].projectName);
        setSelectedUser(newSelectedRows[0].employeeName);
      }
      return;
    }

    // Single mode logic - strictly allow only one timesheet at a time
    if (newSelectedRows.length > 1) {
      // If multiple rows are selected, keep only the last selected one
      const lastSelectedRow = newSelectedRows[newSelectedRows.length - 1];
      const singleRowArray = [lastSelectedRow];

      setSelectedProject(lastSelectedRow.projectName);
      setSelectedUser(lastSelectedRow.employeeName);
      setSelectedRows(singleRowArray);
      return;
    }

    // Single row selection
    const selectedRow = newSelectedRows[0];
    setSelectedProject(selectedRow.projectName);
    setSelectedUser(selectedRow.employeeName);
    setSelectedRows(newSelectedRows);
  }, [isMultipleMode]);

  const isRowDisabled = useCallback((row) => {
    if (isMultipleMode) {
      // In multiple mode, all rows are enabled
      return false;
    }

    // In single mode, if any timesheet is selected, disable all other timesheets
    if (selectedRows.length > 0) {
      // Check if this row is the currently selected one
      const isCurrentlySelected = selectedRows.some(selectedRow => selectedRow.id === row.id);
      return !isCurrentlySelected; // Disable all rows except the currently selected one
    }

    return false; // No rows selected, so all rows are enabled
  }, [selectedRows, isMultipleMode]);

  const handleGenerateInvoice = () => {
    if (selectedRows.length === 0) {
      toast.error("No timesheets selected for invoicing.");
      return;
    }

    const payload = {
      timesheetIds: selectedRows.map((row) => row.id),
      ratePerHour: 0,
    };

    dispatch(requestGenerateInvoice(payload));
    setClearSelectedRows(true);
    dispatch(setResetGenerateInvoice());

    // Refresh the appropriate data based on current mode
    if (isMultipleMode && formikRef.current?.values?.employee && formikRef.current?.values?.project) {
      // In multiple mode, refresh the employee/project specific data
      const params = {
        eId: formikRef.current.values.employee,
        pId: formikRef.current.values.project
      };
      dispatch(requestToBeInvoicedTimesheetListByEmployeeAndProject(params));
    } else {
      // Refresh the main list for single mode
      dispatch(
        requestToBeInvoicedTimesheetListAction({
          page: toBeInvoicedList?.page || 0,
          size: toBeInvoicedList?.size || 10,
          employeeName: search,
          sortby: sortBy,
        })
      );
    }
  };

  const handleModeToggle = () => {
    setIsMultipleMode(!isMultipleMode);
    // Clear all selections when switching modes
    setSelectedRows([]);
    setSelectedProject(null);
    setSelectedUser(null);
    setSelectedEmployeeId(null);
    setClearSelectedRows(true);
    // Clear search when switching modes
    setSearch("");

    // Reset form if it exists
    if (formikRef.current) {
      formikRef.current.resetForm();
    }

    // Clear any existing data from multiple mode
    if (!isMultipleMode) {
      // Switching to multiple mode, clear single mode data
      dispatch(setResetStateToBeInvoicedTimesheetListByEmployeeAndProject());
    } else {
      // Switching to single mode, refresh single mode data
      dispatch(setResetStateToBeInvoicedTimesheetList());
      dispatch(requestToBeInvoicedTimesheetListAction({ page: 0, size: 10, employeeName: "" }));
    }
  };
   // Separate useEffect for sort changes to maintain current page
   useEffect(() => {
    if (isMultipleMode) {
      // For multiple mode, update employee/project specific data with sort
      if (formikRef.current?.values?.employee && formikRef.current?.values?.project) {
        dispatch(requestToBeInvoicedTimesheetListByEmployeeAndProject({
          eId: formikRef.current.values.employee,
          pId: formikRef.current.values.project,
          sortby: sortBy
        }));
      }
    } else {
      // For single mode, update main list with sort
      dispatch(setResetStateToBeInvoicedTimesheetList());
      dispatch(
        requestToBeInvoicedTimesheetListAction({
          page: toBeInvoicedList?.page || 0,
          size: toBeInvoicedList?.size,
          employeeName: search,
          sortby: sortBy,
        })
      );
    }
  }, [sortBy, isMultipleMode, search, toBeInvoicedList?.page, toBeInvoicedList?.size]);

  const columns = [
    {
      name: "Employee Name",
      selector: (row) => row.employeeName,
      sortable: true,
    },
    {
      name: "Project Name",
      selector: (row) => row.projectName,
      sortable: true,
      minWidth: "200px",
    },
    {
      name: "Start Date",
      selector: (row) => moment(row.startDate).format("MM-DD-YYYY"),
     
    },
    {
      name: (
        <div className="flex items-center gap-2 cursor-pointer" onClick={handleEndDateSort}>
          <span>End Date</span>
          {sortBy === 'desc' ? (
            <MdKeyboardArrowUp className="w-4 h-4 text-black-600" />
          ) : (
            <MdKeyboardArrowDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      ),
      selector: (row) => moment(row.endDate).format("MM-DD-YYYY"),
      
    },
    { name: "Location", selector: (row) => row.workLocation, sortable: false },
    { name: "Total Hours", selector: (row) => row.totalHours, sortable: true },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex space-x-4">
          <button
            onClick={() => handleView(row)}
            className="text-primary hover:text-primaryDark"
          >
            <MdVisibility size={24} data-tip data-for="View" />
            <CustomToolTip id="View" title="Details" />
          </button>
          <button
                onClick={() => handleReject(row)}
                className="text-black hover:text-black"
              >
                <MdCancel size={24} data-tip data-for="rejected" />
                <CustomToolTip id="rejected" title="Reject" />
              </button>
        </div>
      ),
    },
  ];

  const handlePageChange = (page) => {
    dispatch(
      requestToBeInvoicedTimesheetListAction({
        page,
        size: toBeInvoicedList?.size,
        employeeName: search,
        sortby: sortBy,
      })
    );
  };

  const handleRowsChange = (size) => {
    dispatch(requestToBeInvoicedTimesheetListAction({
      page: 0,
      size,
      employeeName: search,
      sortby: sortBy,
    }));
  };

  const handleView = (row) => {
    setSelectedRow(row);
    setIsModalOpenView(true);
  };
  const handleReject = (row) => {
    setModalOpen({ open: true, id: row.id });
    // Refresh the appropriate data based on current mode
    if (isMultipleMode && formikRef.current?.values?.employee && formikRef.current?.values?.project) {
      // In multiple mode, refresh the employee/project specific data
      const params = {
        eId: formikRef.current.values.employee,
        pId: formikRef.current.values.project
      };
      dispatch(requestToBeInvoicedTimesheetListByEmployeeAndProject(params));
    } else {
      // Refresh the main list for single mode
      dispatch(requestToBeInvoicedTimesheetListAction({ page: 0, size: 10, employeeName: search, sortby: sortBy }));
    }
  };
  const handleConfirmReject = (id) => {
    if (!reason.trim()) {
      toast.error("Please provide a reason for rejection.");
      return;
    }
    dispatch(
      requestTimesheetsAction({
        id,
        action: "reject",
        remarks: reason,
      })
    );

    toast.success("Timesheet has been rejected.");
    dispatch(setResetTimesheetsAction());
    setModalOpen({ open: false, id: null });
    setReason("");
    
    // Refresh the appropriate data based on current mode
    if (isMultipleMode && formikRef.current?.values?.employee && formikRef.current?.values?.project) {
      // In multiple mode, refresh the employee/project specific data
      const params = {
        eId: formikRef.current.values.employee,
        pId: formikRef.current.values.project
      };
      dispatch(requestToBeInvoicedTimesheetListByEmployeeAndProject(params));
    } else {
      // Refresh the main list for single mode
      dispatch(
        requestToBeInvoicedTimesheetListAction({
          page: 0,
          size: toBeInvoicedList?.size || 10,
          employeeName: search,
          sortby: sortBy,
        })
      );
    }
  };

  const downloadFile = (fileId) => {
    dispatch(requestDownloadFileById({ tId: selectedRow.id, fileId }));
  };

  // Initialize data on component mount
  useEffect(() => {
    dispatch(setResetStateToBeInvoicedTimesheetList());
    dispatch(requestToBeInvoicedTimesheetListAction({ page: 0, size: 10, employeeName: "" ,sortby: sortBy}));
    dispatch(requestUsersListActionWithOutPagination("USER"));
    dispatch(setResetStateProjectsById());
  }, [dispatch]);

  // Handle API responses and errors
  useEffect(() => {
    // Handle toBeInvoicedList errors
    if (toBeInvoicedList?.error && !toBeInvoicedList?.loading) {
      toast.error(toBeInvoicedList?.error?.message);
      dispatch(setResetStateToBeInvoicedTimesheetList());
    }

    // Handle toBeInvoicedByEmployeeAndProject errors
    if (toBeInvoicedByEmployeeAndProject?.error && !toBeInvoicedByEmployeeAndProject?.loading) {
      const errorMessage = toBeInvoicedByEmployeeAndProject?.error?.message;
      
      dispatch(setResetStateToBeInvoicedTimesheetListByEmployeeAndProject());
    }

    // Handle generateInvoice responses
    if (generateInvoice?.response && !generateInvoice?.loading) {
      toast.success("Invoice successfully generated!");
      dispatch(setResetGenerateInvoice());
      
      // Refresh the appropriate data based on current mode
      if (isMultipleMode && formikRef.current?.values?.employee && formikRef.current?.values?.project) {
        // In multiple mode, refresh the employee/project specific data
        const params = {
          eId: formikRef.current.values.employee,
          pId: formikRef.current.values.project
        };
        dispatch(requestToBeInvoicedTimesheetListByEmployeeAndProject(params));
      } else {
        // Refresh the main list for single mode
        dispatch(
          requestToBeInvoicedTimesheetListAction({
            page: toBeInvoicedList?.page || 0,
            size: toBeInvoicedList?.size || 10,
            employeeName: search,
            sortby: sortBy,
          })
        );
      }
    }
    if (generateInvoice?.error && !generateInvoice?.loading) {
      toast.error(generateInvoice?.error?.message);
      dispatch(setResetGenerateInvoice());
    }

    // Handle downloadFileById responses
    if (downloadFileById?.error && !downloadFileById?.loading) {
      toast.error(downloadFileById?.error?.message || "Failed to download file. Please try again.");
      dispatch(setResetDownloadFileById());
    }
    if (downloadFileById?.response && !downloadFileById?.loading) {
      toast.success("File downloaded successfully!");
      dispatch(setResetDownloadFileById());
    }
  }, [
    toBeInvoicedList,
    toBeInvoicedByEmployeeAndProject,
    generateInvoice,
    downloadFileById,
    search,
    dispatch
  ]);

  // Handle TimesheetsAction responses for rejection - following PendingTimesheet pattern
  useEffect(() => {
    if (TimesheetAction?.response && !TimesheetAction?.loading) {
      // Refresh the appropriate data based on current mode
      if (isMultipleMode && formikRef.current?.values?.employee && formikRef.current?.values?.project) {
        // In multiple mode, refresh the employee/project specific data
        const params = {
          eId: formikRef.current.values.employee,
          pId: formikRef.current.values.project
        };
        dispatch(requestToBeInvoicedTimesheetListByEmployeeAndProject(params));
      } else {
        // Refresh the main list for single mode
        dispatch(
          requestToBeInvoicedTimesheetListAction({
            page: toBeInvoicedList?.page || 0,
            size: toBeInvoicedList?.size || 10,
            employeeName: search,
            sortby: sortBy,
          })
        );
      }
      dispatch(setResetTimesheetsAction());
    }
    if (TimesheetAction?.error && !TimesheetAction?.loading) {
      toast.error("Something went wrong");
      dispatch(setResetTimesheetsAction());
    }
  }, [TimesheetAction, isMultipleMode, search, sortBy, toBeInvoicedList?.page, toBeInvoicedList?.size, dispatch]);

  // Clear selected rows
  useEffect(() => {
    if (clearSelectedRows) {
      setSelectedRows([]);
      setSelectedProject(null);
      setSelectedUser(null);
      setClearSelectedRows(false);
    }
  }, [clearSelectedRows]);

  // Auto-select first project when employee changes
  useEffect(() => {
    if (
      selectedEmployeeId &&
      projectsList?.response?.length > 0 &&
      formikRef.current &&
      formikRef.current.values.employee === selectedEmployeeId && // Ensure projects belong to current employee
      !isFetchingProjects // Don't trigger during project fetching
    ) {
      const firstProjectId = projectsList.response[0].id;
      formikRef.current.setFieldValue("project", firstProjectId);
      
      // Reset fetching state
      setIsFetchingProjects(false);
      
      // Trigger API call with the auto-selected project
      const params = {
        eId: selectedEmployeeId,
        pId: firstProjectId
      };
      dispatch(requestToBeInvoicedTimesheetListByEmployeeAndProject(params));
    }
  }, [projectsList, selectedEmployeeId, dispatch, isFetchingProjects]);

  // Reset fetching state when projects are loaded but no auto-selection happens
  useEffect(() => {
    if (
      isFetchingProjects &&
      projectsList?.response?.length > 0 &&
      selectedEmployeeId &&
      formikRef.current?.values.employee === selectedEmployeeId &&
      !formikRef.current?.values.project // No project is selected yet
    ) {
      setIsFetchingProjects(false);
    }
  }, [projectsList, selectedEmployeeId, isFetchingProjects]);

  // Search debouncing for single mode only
  useEffect(() => {
    if (isMultipleMode) return; // Don't trigger search in multiple mode

    const timeoutId = setTimeout(() => {
      dispatch(setResetStateToBeInvoicedTimesheetList());
      dispatch(
        requestToBeInvoicedTimesheetListAction({
          page: 0,
          size: toBeInvoicedList?.size || 10,
          employeeName: search,
          sortby: sortBy,
        })
      );
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search, toBeInvoicedList?.size, dispatch, isMultipleMode]);

  // Client-side search for multiple mode
  const getFilteredData = useCallback(() => {
    if (!toBeInvoicedByEmployeeAndProject?.response) return [];

    if (!search.trim()) return toBeInvoicedByEmployeeAndProject.response;

    return toBeInvoicedByEmployeeAndProject.response.filter(item =>
      item.employeeName?.toLowerCase().includes(search.toLowerCase()) ||
      item.projectName?.toLowerCase().includes(search.toLowerCase()) ||
      item.workLocation?.toLowerCase().includes(search.toLowerCase())
    );
  }, [toBeInvoicedByEmployeeAndProject?.response, search]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(setResetStateToBeInvoicedTimesheetList());
      dispatch(setResetStateToBeInvoicedTimesheetListByEmployeeAndProject());
      dispatch(setResetTimesheetsAction());
    };
  }, [dispatch]);

  const isLoading = toBeInvoicedList.loading ||
    downloadFileById?.loading ||

    toBeInvoicedByEmployeeAndProject?.loading ||
    TimesheetAction?.loading;

  // Modal component
  const DetailsModal = ({ isOpen, onClose, data, onDownload, isDownloading }) => {
    if (!isOpen || !data) return null;

    // Use Formik for the modal form
    return (
      <Formik
        initialValues={{
          projectName: data?.projectName || '',
          employeeName: data?.employeeName || '',
          workLocation: data?.workLocation || '',
          status: data?.status || '',
          startDate: data?.startDate ? moment(data.startDate).format("MM-DD-YYYY") : '',
          endDate: data?.endDate ? moment(data.endDate).format("MM-DD-YYYY") : '',
          totalHours: data?.totalHours || '',
          remarks: data?.remarks || '',
        }}
        enableReinitialize
        onSubmit={() => {}} // No submit action, just for display
      >
        {({ values }) => (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-0 rounded-2xl shadow-2xl w-11/12 md:w-3/4 lg:w-2/4 relative z-50 max-h-[calc(100vh-0.5rem)] border border-gray-200">
              <div className="overflow-y-auto" style={{ maxHeight: '90vh' }}>
                {/* Modal Header */}
                <div className="sticky top-0 bg-white z-10 border-b border-gray-200 flex items-center justify-between px-6 py-4 gap-4 rounded-t-2xl">
                  <div className="flex items-center gap-3">
                    <h2 className="text-primary text-2xl font-bold tracking-tight">Timesheet Details</h2>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-primary hover:text-primaryDark transition-colors p-2 rounded-full focus:outline-none absolute top-4 right-4"
                    type="button"
                    aria-label="Close"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <Form>
                  <div className="flex flex-col gap-6 mb-4 px-8 pt-6 pb-2">
                    {/* Row 1 */}
                    <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
                      <div className="w-full">
                        <TextInput
                          handleChange={() => {}}
                          value={values.projectName}
                          name="projectName"
                          legend
                          isDisabled
                          placeholder="Project Name"
                          customClasses="bg-gray-50 border-gray-200"
                        />
                      </div>
                      <div className="w-full">
                        <TextInput
                          handleChange={() => {}}
                          value={values.employeeName}
                          name="employeeName"
                          legend
                          isDisabled
                          placeholder="Employee Name"
                          customClasses="bg-gray-50 border-gray-200"
                        />
                      </div>
                    </div>
                    {/* Row 2 */}
                    <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
                      <div className="w-full">
                        <TextInput
                          handleChange={() => {}}
                          value={values.workLocation}
                          name="workLocation"
                          legend
                          isDisabled
                          placeholder="Work Location"
                          customClasses="bg-gray-50 border-gray-200"
                        />
                      </div>
                      <div className="w-full">
                        <TextInput
                          handleChange={() => {}}
                          value={values.status}
                          name="status"
                          legend
                          isDisabled
                          placeholder="Status"
                          customClasses="bg-gray-50 border-gray-200"
                        />
                      </div>
                    </div>
                    {/* Row 3 */}
                    <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
                      <div className="w-full">
                        <TextInput
                          handleChange={() => {}}
                          value={values.startDate}
                          name="startDate"
                          legend
                          isDisabled
                          placeholder="Start Date"
                          customClasses="bg-gray-50 border-gray-200"
                        />
                      </div>
                      <div className="w-full">
                        <TextInput
                          handleChange={() => {}}
                          value={values.endDate}
                          name="endDate"
                          legend
                          isDisabled
                          placeholder="End Date"
                          customClasses="bg-gray-50 border-gray-200"
                        />
                      </div>
                      <div className="w-full">
                      <TextInput
                          handleChange={() => {}}
                          value={values.totalHours !== undefined && values.totalHours !== null ? String(values.totalHours) : "0"}
                          name="totalHours"
                          legend
                          isDisabled
                          placeholder="Total Hours"
                          customClasses="bg-gray-50 border-gray-200"
                        />
                      </div>
                    </div>
                    {/* Rejection Reason */}
                    {values.status === 'REJ' && values.remarks && (
                      <div className="w-full">
                        <TextInput
                          handleChange={() => {}}
                          value={values.remarks}
                          name="remarks"
                          legend
                          isDisabled
                          placeholder="Rejection Reason"
                          customClasses="bg-red-50 text-rose-600 border-rose-200"
                        />
                      </div>
                    )}
                  </div>
                </Form>

                {/* Section Divider */}
                <div className="px-6">
                  <div className="border-t border-dashed border-gray-200" />
                </div>

                {/* Holidays & Attachments Section */}
                <div className="px-6 py-2">
                  <h2 className="text-primary text-lg font-semibold flex items-center">
                    <CalendarToday className="text-primary/70" fontSize="small" /> Holidays & Attachments
                  </h2>

                  {/* Holidays Section */}
                  <div className="my-2">
                    <div className="space-y-3">
                      {data.holidays?.length > 0 ? (
                        data.holidays.map((holiday, idx) => (
                          <div
                            key={holiday.id || idx}
                            className="p-3 border border-gray-100 rounded-lg bg-gray-50 shadow-sm flex flex-col sm:flex-row gap-2 sm:gap-4"
                          >
                            <div className="w-full">
                              <label className="block text-xs text-gray-500 mb-1 font-medium">Holiday Date</label>
                              <div className="bg-white px-3 py-2 rounded text-sm border border-gray-100">
                                {holiday.employee_holiday_date ? moment(holiday.employee_holiday_date).format("MM-DD-YYYY") : "-"}
                              </div>
                            </div>
                            <div className="w-full">
                              <label className="block text-xs text-gray-500 mb-1 font-medium">Notes</label>
                              <div className="bg-white px-3 py-2 rounded text-sm border border-gray-100">{holiday.notes || "-"}</div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center p-4 bg-gray-50 rounded-lg border border-dashed border-gray-200 flex items-center justify-center">
                          <p className="text-gray-400 text-xs sm:text-sm">
                            No holidays in this timesheet.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Attachments Section */}
                  <div className="my-2">
                    <h2 className="text-primary text-lg font-semibold flex items-center">
                      <MdAttachFile size={20} /> Attachments
                      </h2>
                    <div className="space-y-3 max-h-40 overflow-y-scroll noScrollBarWidth">
                      {data.files?.length > 0 ? (
                        data.files.map((file) => (
                          <div
                            key={file.id}
                            className="flex items-center justify-between p-3 border border-gray-100 rounded-lg bg-gray-50 shadow-sm"
                          >
                            <div className="flex-1 truncate">
                              <span className="text-gray-700 text-sm font-medium">{file.fileName}</span>
                            </div>
                            <MdDownload
                              className={`cursor-pointer w-6 h-6 ml-2 transition-colors duration-150 ${isDownloading
                                ? 'text-gray-400 animate-pulse'
                                : 'text-green-600 hover:text-green-700 hover:scale-110'
                              }`}
                              onClick={() => !isDownloading && onDownload(file.id)}
                              data-tip
                              data-for={`download-${file.id}`}
                            />
                            <CustomToolTip
                              id={`download-${file.id}`}
                              title={isDownloading ? "Downloading..." : "Download File"}
                            />
                          </div>
                        ))
                      ) : (
                        <div className="text-center p-4 bg-gray-50 rounded-lg border border-dashed border-gray-200 flex items-center justify-center">
                          <p className="text-gray-400 text-xs sm:text-sm">
                            No files available.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Formik>
    );
  };

  return (
    <div className="w-full p-6">
      <Loader isLoading={isLoading} />
      <ModalComponent
        show={modalOpen.open}
        classes={"w-1/3 "}
        onClose={() => {
          setModalOpen({ open: false, id: "" });
          setReason("");
        }}
        title="Reason For Rejection"
      >
        <textarea
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="border p-2 my-2 w-full"
        />
        <div className="flex justify-end mt-4">
          <button
            onClick={() => {
              setModalOpen({ open: false, id: "" });
              setReason("");
            }}
            className="bg-gray-500 text-white px-4 py-2 rounded mr-2"
          >
            Cancel
          </button>
          <button
            onClick={() => handleConfirmReject(modalOpen?.id)}
            className="bg-primary text-white px-4 py-2 rounded"
          >
            Confirm
          </button>
        </div>
      </ModalComponent>

      {/* Header Actions */}
      <div className="absolute flex items-center gap-4 mb-4 z-10 right-6">
        <button
          type="button"
          onClick={handleModeToggle}
          className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 min-w-[150px] ${isMultipleMode
              ? 'bg-primary text-white border-primary hover:bg-primaryDark'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
        >
          {isMultipleMode ? (
            <>
              <MdCheckBox size={20} />
              <span className="font-medium">Batch Invoice </span>
            </>
          ) : (
            <>
              <MdCheckBoxOutlineBlank size={20} />
              <span className="font-medium">Batch Invoice </span>
            </>
          )}
        </button>

        <button
          type="button"
          onClick={handleGenerateInvoice}
          className={`flex items-center justify-center px-6 py-2 rounded-lg transition-all duration-200 min-w-[180px] font-medium ${selectedRows.length > 0
              ? "bg-primary text-white hover:bg-primaryDark"
              : "bg-gray-400 text-gray-100 cursor-not-allowed"
            }`}
          disabled={selectedRows.length === 0}
        >
          Generate Invoice {selectedRows.length > 0 && `(${selectedRows.length})`}
        </button>
      </div>

      {/* Conditional rendering based on mode */}
      {isMultipleMode ? (
        <div>
          <Formik
            innerRef={formikRef}
            initialValues={{
              employee: "",
              project: "",
            }}
            onSubmit={(values) => {
              // Pass IDs instead of names for API call
              if (values.employee && values.project) {
                const params = {
                  eId: values.employee, // Employee ID
                  pId: values.project   // Project ID
                };
                // Dispatch with IDs for API call
                dispatch(requestToBeInvoicedTimesheetListByEmployeeAndProject(params));
              }
            }}
          >
            {({ values, setFieldValue, handleSubmit, errors, touched }) => (
              <Form className="flex flex-wrap lg:flex-nowrap gap-4 items-center justify-start">
                <div className="lg:w-1/4 md:w-1/4 w-full relative">
                  <CustomDropdown
                    value={usersList?.response?.find((user) => user.id === values?.employee) || null}
                    name="employee"
                    handleChange={(selectedOption) => {
                      if (selectedOption?.id) {
                        // Set fetching state to prevent API calls during transition
                        setIsFetchingProjects(true);
                        
                        // Clear existing project selection and reset form
                        setFieldValue("project", "");
                        setSelectedEmployeeId(selectedOption.id);
                        setFieldValue("employee", selectedOption.id);
                        
                        // Clear any existing timesheet data
                        dispatch(setResetStateToBeInvoicedTimesheetListByEmployeeAndProject());
                        
                        // Clear projects list to prevent stale data
                        dispatch(setResetStateProjectsById());
                        
                        // Fetch projects for the new employee
                        dispatch(requestProjectsById(selectedOption.id));
                      }
                    }}
                    customClasses="w-full text-left"
                    placeholder="Select Employee"
                    options={usersList?.response || []}
                    handleGetOptionValue={(option) => option.id}
                    handleGetOptionName={(option) => `${option.firstName} ${option.lastName}`}
                  />
                  {errors.employee && touched.employee && (
                    <p className="text-red-400 text-sm mt-1">{errors.employee}</p>
                  )}
                </div>

                <div className="lg:w-1/4 md:w-1/4 w-full relative">
                  <CustomDropdown
                    value={values?.project ? projectsList?.response?.find((project) => project.id === values?.project) || null : null}
                    name="project"
                    handleChange={(selectedOption) => {
                      if (selectedOption?.id) {
                        setFieldValue("project", selectedOption.id);
                        handleSubmit(); // This will trigger onSubmit with both IDs
                      }
                    }}
                    customClasses="w-full text-left h-10"
                    placeholder="Select Project"
                    options={projectsList?.response || []}
                    handleGetOptionValue={(option) => option.id}
                    handleGetOptionName={(option) => option.projectName}
                    isDisabled={!values.employee || !projectsList?.response?.length}
                  />
                  {errors.project && touched.project && (
                    <p className="text-red-400 text-sm mt-1">{errors.project}</p>
                  )}
                </div>
              </Form>
            )}
          </Formik>

          {/* Data table for multiple mode results */}
        
            
            <div className="mt-6">
              <CustomDataTable
                columns={columns}
                data={getFilteredData()|| []}
                //search={search}
                //setSearch={setSearch}
                //enableSearch={true}
                onSelectedRowsChange={handleCheckboxChange}
                selectableRows={true}
                selectedRows={selectedRows}
                selectableRowDisabled={isRowDisabled}
                clearSelectedRows={clearSelectedRows}
                fixedHeaderScrollHeight="62vh"
                pagination={false}
                paginationServer={false}
                isMultiSelect={false}
                selectableRowsHighlight={true}

                
              />
            </div>
          
        </div>
      ) : (
        <div>
          <CustomDataTable
            columns={columns}
            data={toBeInvoicedList?.response?.content || []}
            search={search}
            setSearch={setSearch}
            enableSearch={true}
            serverPagenation
            paginationTotalRows={toBeInvoicedList?.response?.totalElements}
            handleChangePage={handlePageChange}
            handleRowsChange={handleRowsChange}
            onSelectedRowsChange={handleCheckboxChange}
            selectableRows={true}
            selectedRows={selectedRows}
            selectableRowDisabled={false}
            clearSelectedRows={clearSelectedRows}
            fixedHeaderScrollHeight="62vh"
            isMutiSelect={false}
            selectableRowsHighlight={false}
            
          />
        </div>
      )}

      {/* Details Modal */}
      <DetailsModal
        isOpen={isModalOpenView}
        onClose={() => setIsModalOpenView(false)}
        data={selectedRow}
        onDownload={downloadFile}
        isDownloading={downloadFileById?.loading}
      />
    </div>
  );
};

export default Hoc(ToBeInvoiced);